'use client'

import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package,
  DollarSign,
  Tag,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'

interface Servico {
  id: string
  nome: string
  descricao?: string
  valor_padrao: number
  categoria?: string
  ativo: boolean
  servico_avulso?: boolean
  created_at: string
  updated_at: string
}

export default function ServicosPage() {
  const [servicos, setServicos] = useState<Servico[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [editingServico, setEditingServico] = useState<Servico | null>(null)
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    valor_padrao: 0,
    categoria: '',
    ativo: true,
    servico_avulso: false
  })
  const [valorDisplay, setValorDisplay] = useState('')
  const [isEditingValor, setIsEditingValor] = useState(false)

  useEffect(() => {
    fetchServicos()
  }, [])

  const fetchServicos = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/servicos')
      const data = await response.json()
      if (data.data) {
        setServicos(data.data)
      }
    } catch (error) {
      console.error('Erro ao carregar serviços:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      const url = editingServico ? `/api/servicos?id=${editingServico.id}` : '/api/servicos'
      const method = editingServico ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok) {
        await fetchServicos()
        setShowModal(false)
        resetForm()
      } else {
        console.error('Erro ao salvar serviço:', result.error)
        alert('Erro ao salvar serviço: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar serviço:', error)
      alert('Erro ao salvar serviço')
    }
  }

  const handleDelete = async (servicoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este serviço?')) {
      return
    }

    try {
      const response = await fetch(`/api/servicos?id=${servicoId}`, {
        method: 'DELETE'
      })

      const result = await response.json()

      if (response.ok) {
        await fetchServicos()
      } else {
        alert('Erro ao excluir serviço: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao excluir serviço:', error)
      alert('Erro ao excluir serviço')
    }
  }

  const openEditModal = (servico: Servico) => {
    setEditingServico(servico)
    setFormData({
      nome: servico.nome,
      descricao: servico.descricao || '',
      valor_padrao: servico.valor_padrao,
      categoria: servico.categoria || '',
      ativo: servico.ativo,
      servico_avulso: servico.servico_avulso || false
    })
    setValorDisplay(servico.valor_padrao > 0 ? servico.valor_padrao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
    setIsEditingValor(false)
    setShowModal(true)
  }

  const resetForm = () => {
    setEditingServico(null)
    setFormData({
      nome: '',
      descricao: '',
      valor_padrao: 0,
      categoria: '',
      ativo: true,
      servico_avulso: false
    })
    setValorDisplay('')
    setIsEditingValor(false)
  }

  const filteredServicos = servicos.filter(servico => {
    const matchesSearch = (servico.nome?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (servico.categoria?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (servico.descricao?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'ativo' && servico.ativo) ||
                         (statusFilter === 'inativo' && !servico.ativo)
    
    return matchesSearch && matchesStatus
  })

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const getStatusIcon = (ativo: boolean) => {
    return ativo ? 
      <CheckCircle className="w-4 h-4 text-green-600" /> : 
      <XCircle className="w-4 h-4 text-red-600" />
  }

  const getStatusColor = (ativo: boolean) => {
    return ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <span className="text-gray-600">Carregando serviços...</span>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Catálogo de Serviços</h1>
                  <p className="text-sm text-gray-600">Gerencie os serviços oferecidos pela empresa</p>
                </div>
                <button
                  onClick={() => {
                    resetForm()
                    setShowModal(true)
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Novo Serviço</span>
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Filtros */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Buscar por nome, categoria ou descrição..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="all">Todos os status</option>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lista de Serviços */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {filteredServicos.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum serviço encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {servicos.length === 0 
                      ? 'Comece criando um novo serviço para seu catálogo.'
                      : 'Tente ajustar os filtros de busca.'
                    }
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Serviço
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Categoria
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Padrão
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredServicos.map((servico) => (
                        <tr key={servico.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {servico.nome}
                              </div>
                              {servico.descricao && (
                                <div className="text-sm text-gray-500 max-w-xs truncate">
                                  {servico.descricao}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {servico.categoria ? (
                              <div className="flex items-center">
                                <Tag className="w-3 h-3 mr-1 text-gray-400" />
                                <span className="text-sm text-gray-900">{servico.categoria}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Sem categoria</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DollarSign className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(servico.valor_padrao)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(servico.ativo)}`}>
                              {getStatusIcon(servico.ativo)}
                              <span className="ml-1">{servico.ativo ? 'Ativo' : 'Inativo'}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditModal(servico)}
                                className="text-orange-600 hover:text-orange-900 transition-colors"
                                title="Editar serviço"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(servico.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Excluir serviço"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Modal de Criação/Edição */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    {editingServico ? 'Editar Serviço' : 'Novo Serviço'}
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Nome do Serviço */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Serviço *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: Marketing Digital, SEO, Tráfego Pago"
                      />
                    </div>

                    {/* Categoria */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Categoria
                      </label>
                      <input
                        type="text"
                        value={formData.categoria}
                        onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Ex: Marketing, Desenvolvimento, Consultoria"
                      />
                    </div>

                    {/* Valor Padrão */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor Padrão Mensal *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          R$
                        </span>
                        <input
                          type="text"
                          value={valorDisplay}
                          onChange={(e) => {
                            // Remove tudo que não é número
                            const numbersOnly = e.target.value.replace(/\D/g, '');
                            
                            if (numbersOnly === '') {
                              setValorDisplay('');
                              setFormData({ ...formData, valor_padrao: 0 });
                              return;
                            }
                            
                            // Converte para número (centavos)
                            const numericValue = parseInt(numbersOnly) / 100;
                            
                            // Formata em tempo real como moeda brasileira
                            const formatted = numericValue.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            });
                            
                            setValorDisplay(formatted);
                            setFormData({ ...formData, valor_padrao: numericValue });
                          }}
                          onFocus={() => {
                            setIsEditingValor(true);
                            // Se o valor é 0, deixa vazio para facilitar digitação
                            if (formData.valor_padrao === 0) {
                              setValorDisplay('');
                            } else {
                              setValorDisplay(formData.valor_padrao.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }));
                            }
                          }}
                          onBlur={() => {
                            setIsEditingValor(false);
                            // Garante formatação final
                            if (formData.valor_padrao > 0) {
                              setValorDisplay(formData.valor_padrao.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }));
                            }
                          }}
                          required
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="0,00"
                        />
                      </div>
                    </div>

                    {/* Descrição */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Descreva o serviço..."
                      />
                    </div>

                    {/* Serviço Avulso */}
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="servico_avulso"
                        checked={formData.servico_avulso}
                        onChange={(e) => setFormData({ ...formData, servico_avulso: e.target.checked })}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <label htmlFor="servico_avulso" className="text-sm font-medium text-gray-700">
                        Serviço Avulso
                      </label>
                      <span className="text-xs text-gray-500">
                        (Serviço com pagamento separado do plano mensal)
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.ativo}
                          onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Serviço ativo (disponível para novos planos)</span>
                      </label>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        {editingServico ? 'Atualizar' : 'Criar'} Serviço
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
